import { Eye, EyeOff } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const API_BASE_URL = "/api/v1";

  async function handleSubmit(e?: React.MouseEvent) {
    if (e) e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError("Please enter email and password.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      setSubmitting(false);

      if (!res.ok) {
        let message = data.detail || "Login failed";
        setError(message);
        return;
      }

      const token = data.access_token || data.token;

      const roleName =
        typeof data.user.role === "string"
          ? data.user.role
          : data.user.role?.name;

      localStorage.setItem("userId", data.user.id);
      localStorage.setItem("userRole", roleName);
      localStorage.setItem("token", token);

      if (roleName === "customer_care") {
        navigate("/customer-care");
      } else {
        navigate("/overview");
      }
    } catch (err) {
      console.error(err);
      setError("Network error");
      setSubmitting(false);
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  type PromoSlide = {
    id: number;
    label: string;
    title: string;
    description: string;
    highlight: string;
    stats: { label: string; value: string; trend?: string }[];
  };

  const promoSlides: PromoSlide[] = [
    {
      id: 0,
      label: "Customer Management",
      title: "Grow and retain your ISP customers.",
      description:
        "See active accounts, invoices, and router status in one clean dashboard. Keep your team aligned and your customers online.",
      highlight: "Real-time customer overview.",
      stats: [
        {
          label: "Active PPPoE users",
          value: "1,248",
          trend: "+18% this month",
        },
        { label: "Churn rate", value: "1.2%", trend: "Lower than last month" },
      ],
    },
    {
      id: 1,
      label: "Billing & Collections",
      title: "Automated billing that just works.",
      description:
        "Generate invoices, reconcile M-Pesa payments, and disable non-paying accounts automatically. Reduce manual work and missed revenue.",
      highlight: "From invoice to payment in seconds.",
      stats: [
        { label: "On-time payments", value: "93%", trend: "+9% vs last month" },
        {
          label: "Revenue captured",
          value: "KES 1.8M",
          trend: "No manual exports",
        },
      ],
    },
    {
      id: 2,
      label: "Network Health",
      title: "See your network like your customers do.",
      description:
        "Monitor routers, profiles, and PPPoE sessions. Know where issues start before the support tickets arrive.",
      highlight: "Proactive alerts, less downtime.",
      stats: [
        { label: "Online routers", value: "24 / 24", trend: "All green" },
        {
          label: "Avg session uptime",
          value: "19h",
          trend: "Stable connections",
        },
      ],
    },
  ];

  const [activeSlide, setActiveSlide] = useState(0);

  // Auto-rotate slides
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % promoSlides.length);
    }, 8000); // 8 seconds
    return () => clearInterval(interval);
  }, [promoSlides.length]);

  const currentSlide = promoSlides[activeSlide];

  return (
    <div className="min-h-screen flex bg-white relative">
      {/* Left side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 py-12 relative">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-12">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center mb-8">
              <svg
                className="w-7 h-7 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome Back to AAMX
            </h1>
            <p className="text-gray-500 text-sm">
              Enter your username and password to continue.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Form */}
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all text-gray-900"
                placeholder="Enter your email address"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all text-gray-900 pr-12"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                />
                <span className="text-sm text-gray-600">Remember me</span>
              </label>
              <button
                type="button"
                className="text-sm text-gray-900 font-medium hover:text-gray-700"
                onClick={() =>
                  alert("Contact your ISP administrator to reset your password")
                }
              >
                Forgot password
              </button>
            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
            >
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </div>

          {/* Social Login */}
          <div className="mt-6">
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">
                  Or login with
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
                onClick={() => alert("Google login not implemented")}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-700">
                  Google
                </span>
              </button>
              <button
                type="button"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
                onClick={() => alert("Apple login not implemented")}
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                <span className="text-sm font-medium text-gray-700">Apple</span>
              </button>
            </div>
          </div>

          {/* Register Link */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <button
                type="button"
                className="font-semibold text-gray-900 hover:text-gray-700"
                onClick={() => alert("Please contact support")}
              >
                Register
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Dynamic ISP promo slider */}
      <div className="hidden lg:flex lg:w-1/2 bg-gray-900 items-center justify-center p-12">
        <div className="max-w-2xl w-full">
          <div className="space-y-6">
            {/* Top tag */}
            <div className="inline-flex items-center gap-2 rounded-full bg-gray-800/70 px-3 py-1 border border-gray-700/70 mb-2">
              <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-[11px] uppercase tracking-wide text-gray-300">
                {currentSlide.label}
              </span>
            </div>

            {/* Main card - transitions for "live" feel */}
            <div className="bg-gray-800/60 backdrop-blur rounded-2xl p-6 border border-gray-700/60 transition-all duration-500">
              <h2 className="text-2xl font-bold text-white mb-3">
                {currentSlide.title}
              </h2>
              <p className="text-sm text-gray-300 mb-4">
                {currentSlide.description}
              </p>
              <p className="text-sm font-semibold text-orange-300 mb-5">
                {currentSlide.highlight}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {currentSlide.stats.map((s) => (
                  <div
                    key={s.label}
                    className="bg-gray-900/40 rounded-xl px-4 py-3 border border-gray-700/60"
                  >
                    <div className="text-xs text-gray-400 mb-1">{s.label}</div>
                    <div className="text-xl font-semibold text-white">
                      {s.value}
                    </div>
                    {s.trend && (
                      <div className="text-[11px] text-gray-400 mt-1">
                        {s.trend}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Small extra card under main promo */}
            <div className="bg-gray-800/40 backdrop-blur rounded-2xl p-5 border border-gray-700/60">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-100">
                  Why ISPs choose Cusana
                </span>
              </div>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>• One place for customers, billing & routers.</li>
                <li>• Deep Mikrotik integration for PPPoE & queues.</li>
                <li>• Automated actions on non-payment and expiry.</li>
              </ul>
            </div>
          </div>

          {/* Slider dots */}
          <div className="mt-8 flex items-center justify-center gap-2">
            {promoSlides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => setActiveSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === activeSlide
                    ? "w-5 bg-white"
                    : "w-2 bg-gray-600 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
